import { useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import { AdminShopSport, deleteShopSport, reorderShopSports, saveShopSport } from "../../../api/shop";
import { Alert, Button, Card, Dialog, EmptyState, Field, IconButton, Input, Switch, Tag, useDialogs } from "../../../ui";
import { sportTone } from "../shopFormat";
import { codeFromName, normalizeCode } from "../shared/codes";
import { GuardedDelete } from "../shared/GuardedDelete";
import { ReorderControls } from "../shared/ReorderControls";
import { useLatched } from "../shared/useLatched";
import { usePersistedOrder } from "../shared/useReorder";
import { usedByLabel, usedByText } from "./catalogGuards";
import { validateCode } from "./catalogForms";
import type { CatalogTabProps } from "./types";

type SportForm = { code: string; tagline: string; isVisible: boolean };

const SportsTab = ({ catalog, onChanged, showError, showSuccess }: CatalogTabProps) => {
  const { confirm } = useDialogs();
  // Every sport from the app: the set-up ones (ordered, used by products), then the rest
  const setUp = useMemo(() => catalog.sports.filter((s) => s.isSetUp !== false), [catalog.sports]);
  const notSetUp = useMemo(() => catalog.sports.filter((s) => s.isSetUp === false), [catalog.sports]);
  // A saved order is read back into the page's catalog, so it holds after switching tabs
  const order = usePersistedOrder(
    setUp,
    (s) => s.id,
    reorderShopSports,
    (err) => showError(err, "Failed to reorder sports"),
    () => void onChanged()
  );
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AdminShopSport | null>(null);
  const [form, setForm] = useState<SportForm>({ code: "", tagline: "", isVisible: true });
  const [saving, setSaving] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const isNew = editing?.isSetUp === false;
  const title = useLatched(open, editing ? (isNew ? `Set up ${editing.name} for the shop` : `Edit ${editing.name}`) : "");
  const saveLabel = useLatched(open, isNew ? "Set up" : "Update");
  const problem = validateCode(form.code);

  const openSetUp = (sport: AdminShopSport) => {
    setEditing(sport);
    setForm({ code: codeFromName(sport.name), tagline: "", isVisible: true });
    setAttempted(false);
    setOpen(true);
  };

  const openEdit = (sport: AdminShopSport) => {
    setEditing(sport);
    setForm({ code: sport.code, tagline: sport.tagline || "", isVisible: sport.isVisible });
    setAttempted(false);
    setOpen(true);
  };

  const close = () => {
    if (!saving) setOpen(false);
  };

  const handleSave = async () => {
    setAttempted(true);
    if (!editing || problem) return;
    setSaving(true);
    try {
      await saveShopSport(editing.id, { code: form.code, tagline: form.tagline.trim() || null, isVisible: form.isVisible });
      showSuccess(isNew ? `${editing.name} is set up for the shop` : "Sport updated successfully");
      setOpen(false);
      await onChanged();
    } catch (err: any) {
      showError(err, "Failed to save sport");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (sport: AdminShopSport) => {
    const ok = await confirm({
      title: "Remove from the Shop",
      tone: "danger",
      confirmLabel: "Remove",
      message: (
        <>
          Remove "<strong>{sport.name}</strong>" from the shop? Its code and tagline are deleted. The sport itself stays in
          the app.
        </>
      ),
    });
    if (!ok) return;
    try {
      await deleteShopSport(sport.id);
      showSuccess(`${sport.name} removed from the shop`);
      await onChanged();
    } catch (err: any) {
      showError(err, "Failed to remove sport");
    }
  };

  return (
    <>
      <Card padding="none" title="Sports in the shop" subtitle="Products are grouped by these. Sport tiles are built from each sport's products.">
        {order.list.length === 0 ? (
          <EmptyState compact title="No sports set up yet." description="Set up a sport below to tag products with it." />
        ) : (
          <ul className="catalog-list">
            {order.list.map((sport, index) => (
              <li
                key={sport.id}
                className={`catalog-row${sport.isVisible ? "" : " is-muted"}${order.dragIndex === index ? " reorder-dragging" : ""}`}
                {...order.rowProps(index)}
              >
                <ReorderControls
                  index={index}
                  count={order.list.length}
                  onStep={order.step}
                  disabled={order.saving}
                  handleProps={order.handleProps(index)}
                />
                <div className="catalog-row__main">
                  <span className="catalog-row__name">{sport.name}</span>
                  <Tag tone={sportTone(sport.name)} className="code-tag">
                    {sport.code}
                  </Tag>
                  {!sport.isVisible && <Tag tone="danger">Hidden</Tag>}
                  {sport.tagline && <span className="catalog-row__sub">{sport.tagline}</span>}
                </div>
                <span className="catalog-row__meta">{usedByLabel(sport.productCount)}</span>
                <div className="catalog-row__actions">
                  <IconButton label="Edit" icon={<Pencil size={16} />} onClick={() => openEdit(sport)} />
                  <GuardedDelete
                    label="Remove from the shop"
                    blockedReason={usedByText(sport.productCount)}
                    onDelete={() => handleRemove(sport)}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {notSetUp.length > 0 && (
        <Card padding="none" title="Not in the shop yet" subtitle="Sports from the app. Set one up to use it in the shop.">
          <ul className="catalog-list">
            {notSetUp.map((sport) => (
              <li key={sport.id} className="catalog-row catalog-row--plain">
                <div className="catalog-row__main">
                  <span className="catalog-row__name">{sport.name}</span>
                </div>
                <div className="catalog-row__actions">
                  <Button size="sm" variant="tint" onClick={() => openSetUp(sport)}>
                    Set up for the shop
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Dialog
        open={open}
        onClose={close}
        title={title}
        size="sm"
        busy={saving}
        bodyClassName="catalog-form"
        footer={
          <>
            <Button variant="ghost" onClick={close} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving} disabled={!form.code}>
              {saveLabel}
            </Button>
          </>
        }
      >
        <Field label="Code" required hint="1–6 letters or digits: the first part of product codes (PAD-TSH-01). Products with no sport or several use ALL.">
          {(id) => (
            <Input
              id={id}
              value={form.code}
              maxLength={6}
              placeholder="e.g. PAD"
              onChange={(e) => {
                const code = normalizeCode(e.target.value);
                setForm((f) => ({ ...f, code }));
              }}
            />
          )}
        </Field>
        <Field label="Tagline" hint="A short line under the sport's name in the shop">
          {(id) => (
            <Input
              id={id}
              value={form.tagline}
              maxLength={120}
              placeholder="e.g. Rackets, balls and kit for the court"
              onChange={(e) => {
                const tagline = e.target.value;
                setForm((f) => ({ ...f, tagline }));
              }}
            />
          )}
        </Field>
        <Switch
          checked={form.isVisible}
          onChange={(isVisible) => setForm((f) => ({ ...f, isVisible }))}
          label="Visible in the shop"
          description="Shows its tile and filter. A hidden sport keeps its products' tags."
        />
        {attempted && problem && <Alert tone="danger">{problem}</Alert>}
      </Dialog>
    </>
  );
};

export default SportsTab;
